import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Check, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Popover from '../components/Popover';
import { useToast } from '../components/Toast';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import { PageContainer, Card, Button, Input, Select, Label, FormGroup, Spinner, LoadingContainer } from '../components/ui';
import { theme } from '../styles/theme';
import { formatPrice } from '../utils/currency';
import { COUNTRIES, getDocumentConfig, validateDocument, getCountryCurrency } from '../utils/documents';
import { getPasswordError } from '../utils/password';
import api from '../services/api';

const AddTenant = ({ editMode = false }) => {
  const { t } = useTranslation('tenants');
  const toast = useToast();
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(editMode);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [platformTiers, setPlatformTiers] = useState([]);
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [tenant, setTenant] = useState({
    name: '', email: '', phone: '',
    address: { street: '', number: '', complement: '', district: '', city: '', state: '', zip: '', ibge: '' },
    country: 'BR', personType: 'company', document: '',
    tier: 'starter', timezone: 'America/Sao_Paulo',
    adminName: '', adminEmail: '', adminPassword: '', billingEmail: '',
    logRetentionDays: 1
  });

  // Só sallus_admin pode ver/alterar a retenção de logs (Requirement 8.1)
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = currentUser.role === 'sallus_admin';
  const [cepLoading, setCepLoading] = useState(false);

  const STEPS = [t('steps.basicData'), t('steps.tier'), t('steps.admin'), t('steps.review')];

  const breadcrumbItems = [
    { label: 'Libben Agents', icon: '🚀', path: '/sallus/dashboard' },
    { label: editMode ? t('editTenant') : t('newClient'), icon: editMode ? '✏️' : '➕' }
  ];

  useEffect(() => {
    const init = async () => {
      await loadTiers();
      if (editMode && tenantId) await loadTenantData();
    };
    init();
  }, [editMode, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTiers = async () => {
    try {
      setLoadingTiers(true);
      const res = await api.get('/platform/tiers');
      setPlatformTiers(res.data.tiers || []);
    } catch (e) { console.error(e); }
    finally { setLoadingTiers(false); }
  };

  const handleCepBlur = async (cep) => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setTenant(prev => ({
          ...prev,
          address: {
            ...prev.address,
            street: data.logradouro || prev.address.street,
            district: data.bairro || prev.address.district,
            city: data.localidade || prev.address.city,
            state: data.uf || prev.address.state,
            ibge: data.ibge || prev.address.ibge,
            zip: digits
          }
        }));
      }
    } catch (e) { console.warn('ViaCEP error', e); }
    finally { setCepLoading(false); }
  };

  const loadTenantData = async () => {
    try {
      const res = await api.get(`/tenants/${tenantId}`);
      const data = res.data;
      setBillingAnnual(data.billingAnnual || false);
      setTenant({
        name: data.name || '', email: data.email || '', phone: data.phone || '',
        address: data.address || { street: '', number: '', complement: '', district: '', city: '', state: '', zip: '', ibge: '' },
        country: data.country || 'BR',
        personType: data.personType || 'company',
        document: data.document || data.cnpj || '',
        tier: data.tier || 'starter',
        adminName: data.adminName || '', adminEmail: data.adminEmail || '', adminPassword: '',
        billingEmail: data.billingEmail || '',
        timezone: data.timezone || 'America/Sao_Paulo',
        logRetentionDays: data.logRetentionDays ?? 1
      });
    } catch (e) { toast.error(t('loadError')); navigate('/sallus/dashboard'); }
    finally { setLoading(false); }
  };

  // formatação e validação de documento via utilitário documents.js

  const handleNext = () => {
    if (step === 1 && (!tenant.name || !tenant.email)) { toast.warning(t('validationNameEmail')); return; }
    if (step === 2 && !tenant.tier) { toast.warning(t('validationTier')); return; }
    if (step === 3 && (!tenant.adminName || !tenant.adminEmail)) { toast.warning(t('validationAdmin')); return; }
    if (step === 3 && !editMode && !tenant.adminPassword) { toast.warning(t('validationPassword')); return; }
    // Senha obrigatória na criação, opcional na edição; se houver, valida a política do Cognito.
    if (step === 3 && tenant.adminPassword) {
      const pwErr = getPasswordError(tenant.adminPassword);
      if (pwErr) { toast.warning(pwErr); return; }
    }
    if (step < 4) setStep(step + 1);
  };

  const handleSubmit = async () => {
    const finalTenantId = editMode ? tenantId : tenant.name.toLowerCase().replace(/\s+/g, '_');
    const docConfig = getDocumentConfig(tenant.country, tenant.personType);
    const tenantData = {
      tenantId: finalTenantId, ...tenant,
      cnpj: tenant.personType === 'company' && tenant.country === 'BR' ? tenant.document : '',
      documentType: docConfig.type,
      currency: getCountryCurrency(tenant.country),
      // pendingTier usado pelo billing para criar assinatura (mesmo campo do RegisterPage)
      pendingTier: tenant.tier,
      tier: tenant.tier,
      billingAnnual,
      // postalCode e addressNumber no nível raiz — formato exigido pelo Asaas
      postalCode:    tenant.address.zip    ? tenant.address.zip.replace(/\D/g, '')    : '',
      addressNumber: tenant.address.number ? tenant.address.number                    : '',
      status: 'active',
      billingEmail: tenant.billingEmail || tenant.adminEmail || tenant.email,
      address: tenant.address,
      // termsAcceptedAt registra que o admin aceitou os termos em nome do cliente
      termsAcceptedAt: new Date().toISOString(),
      termsVersion: '1.1',
      // Retenção de logs (só sallus_admin; backend também valida e clampa)
      ...(isSallusAdmin ? { logRetentionDays: Math.min(60, Math.max(1, parseInt(tenant.logRetentionDays, 10) || 1)) } : {}),
      ...(editMode ? {} : { createdAt: new Date().toISOString() })
    };
    try {
      await api[editMode ? 'put' : 'post'](editMode ? `/tenants/${finalTenantId}` : '/tenants', tenantData);
      toast.success(t('saveSuccess', { name: tenant.name, action: editMode ? t('updated') : t('created') }));
      navigate(editMode ? '/sallus/dashboard' : `/dashboard/${finalTenantId}`);
    } catch (e) { toast.error(t('saveError', { msg: e.response?.data?.message || e.message })); }
  };

  if (loading) return (
    <>
      <AppHeader title={editMode ? t('editTenant') : t('newClient')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <LoadingContainer><Spinner size="24px" /> {t('loadError')}</LoadingContainer>
    </>
  );

  return (
    <>
      <AppHeader title={editMode ? t('editTenant') : t('newClient')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <WizardCard>
          <StepsRow>
            {STEPS.map((label, i) => {
              const n = i + 1; const done = step > n; const active = step >= n;
              return (
                <React.Fragment key={n}>
                  <StepItem active={active}>
                    <StepCircle active={active} done={done}>{done ? <Check size={14} /> : n}</StepCircle>
                    <StepLabel active={active}>{label}</StepLabel>
                  </StepItem>
                  {i < STEPS.length - 1 && <StepLine done={done} />}
                </React.Fragment>
              );
            })}
          </StepsRow>

          <StepContent>
            {step === 1 && (
              <Section>
                <SectionTitle>{t('step1Title')}</SectionTitle>
                <FormGrid>
                  <FormGroup><Label>{t('clientName')}</Label><Input value={tenant.name} onChange={e => setTenant({ ...tenant, name: e.target.value })} placeholder={t('clientNamePlaceholder')} /></FormGroup>
                  <FormGroup><Label>{t('email')}</Label><Input type="email" value={tenant.email} onChange={e => setTenant({ ...tenant, email: e.target.value })} placeholder={t('emailPlaceholder')} /></FormGroup>
                  <FormGroup><Label>{t('phone')}</Label><Input value={tenant.phone} onChange={e => setTenant({ ...tenant, phone: e.target.value })} placeholder={t('phonePlaceholder')} /></FormGroup>
                  <FormGroup>
                    <Label>{t('country')}</Label>
                    <Select value={tenant.country} onChange={e => setTenant({ ...tenant, country: e.target.value, document: '' })}>
                      {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {t(`countries.${c.code}`, { defaultValue: c.name })}</option>)}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label>{t('timezone')}</Label>
                    <Select value={tenant.timezone || 'America/Sao_Paulo'} onChange={e => setTenant({ ...tenant, timezone: e.target.value })}>
                      <option value="America/Sao_Paulo">America/Sao_Paulo (BRT)</option>
                      <option value="America/Manaus">America/Manaus (AMT)</option>
                      <option value="America/Belem">America/Belem (BRT)</option>
                      <option value="America/Fortaleza">America/Fortaleza (BRT)</option>
                      <option value="America/Recife">America/Recife (BRT)</option>
                      <option value="America/Noronha">America/Noronha (FNT)</option>
                      <option value="America/New_York">America/New_York (EST)</option>
                      <option value="America/Chicago">America/Chicago (CST)</option>
                      <option value="America/Denver">America/Denver (MST)</option>
                      <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                      <option value="America/Mexico_City">America/Mexico_City (CST)</option>
                      <option value="America/Bogota">America/Bogota (COT)</option>
                      <option value="America/Lima">America/Lima (PET)</option>
                      <option value="America/Santiago">America/Santiago (CLT)</option>
                      <option value="America/Buenos_Aires">America/Buenos_Aires (ART)</option>
                      <option value="Europe/Lisbon">Europe/Lisbon (WET)</option>
                      <option value="Europe/London">Europe/London (GMT)</option>
                      <option value="Europe/Madrid">Europe/Madrid (CET)</option>
                      <option value="UTC">UTC</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <LabelWithHelp>
                      <Label style={{ margin: 0 }}>{t('personType')}</Label>
                      <Popover content={t('tooltips.personType')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
                    </LabelWithHelp>
                    <Select value={tenant.personType} onChange={e => setTenant({ ...tenant, personType: e.target.value, document: '' })}>
                      <option value="company">{t('personTypeCompany')}</option>
                      <option value="individual">{t('personTypeIndividual')}</option>
                    </Select>
                  </FormGroup>
                </FormGrid>
                {tenant.country === 'BR' && (
                  <FormGrid style={{ marginTop: theme.spacing[4] }}>
                    <FormGroup>
                      <Label>{t('zip')}</Label>
                      <div style={{ position: 'relative' }}>
                        <Input
                          value={tenant.address.zip}
                          onChange={e => setTenant({ ...tenant, address: { ...tenant.address, zip: e.target.value } })}
                          onBlur={e => handleCepBlur(e.target.value)}
                          placeholder="00000-000"
                          maxLength={9}
                        />
                        {cepLoading && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}><Spinner size="14px" /></span>}
                      </div>
                    </FormGroup>
                    <FormGroup />
                  </FormGrid>
                )}
                <FormGrid style={{ marginTop: theme.spacing[3] }}>
                  <FormGroup>
                    <Label>{t('street')}</Label>
                    <Input value={tenant.address.street} onChange={e => setTenant({ ...tenant, address: { ...tenant.address, street: e.target.value } })} placeholder={t('addressPlaceholder')} />
                  </FormGroup>
                  <FormGroup>
                    <Label>{t('number')}</Label>
                    <Input value={tenant.address.number} onChange={e => setTenant({ ...tenant, address: { ...tenant.address, number: e.target.value } })} placeholder="123" />
                  </FormGroup>
                  <FormGroup>
                    <Label>{t('district')}</Label>
                    <Input value={tenant.address.district} onChange={e => setTenant({ ...tenant, address: { ...tenant.address, district: e.target.value } })} placeholder={t('districtPlaceholder')} />
                  </FormGroup>
                  <FormGroup>
                    <Label>{t('complement')}</Label>
                    <Input value={tenant.address.complement} onChange={e => setTenant({ ...tenant, address: { ...tenant.address, complement: e.target.value } })} placeholder={t('complementPlaceholder')} />
                  </FormGroup>
                  <FormGroup>
                    <Label>{t('city')}</Label>
                    <Input value={tenant.address.city} onChange={e => setTenant({ ...tenant, address: { ...tenant.address, city: e.target.value } })} placeholder={t('cityPlaceholder')} />
                  </FormGroup>
                  <FormGroup>
                    <Label>{t('state')}</Label>
                    <Input value={tenant.address.state} onChange={e => setTenant({ ...tenant, address: { ...tenant.address, state: e.target.value } })} placeholder={t('statePlaceholder')} />
                  </FormGroup>
                </FormGrid>
                {(() => {
                  const docCfg = getDocumentConfig(tenant.country, tenant.personType);
                  const isInvalid = tenant.document && !validateDocument(tenant.document, tenant.country, tenant.personType);
                  return (
                    <FormGroup style={{ marginTop: theme.spacing[4] }}>
                      <LabelWithHelp>
                        <Label style={{ margin: 0 }}>{docCfg.label}{docCfg.required ? ' *' : ` (${t('documentOptional')})`}</Label>
                        <Popover content={t('tooltips.document')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
                      </LabelWithHelp>
                      <Input
                        value={tenant.document}
                        onChange={e => setTenant({ ...tenant, document: docCfg.mask(e.target.value) })}
                        placeholder={docCfg.placeholder}
                      />
                      {isInvalid && <HelperErr>{t('documentInvalid')}</HelperErr>}
                    </FormGroup>
                  );
                })()}
              </Section>
            )}

            {step === 2 && (
              <Section>
                <LabelWithHelp style={{ marginBottom: theme.spacing[2] }}>
                  <SectionTitle style={{ margin: 0 }}>{t('step2Title')}</SectionTitle>
                  <Popover content={t('tooltips.tier')}><HelpCircle size={14} color={theme.colors.textMuted} /></Popover>
                </LabelWithHelp>
                <Helper style={{ marginBottom: theme.spacing[5] }}>{t('step2Hint')}</Helper>
                {loadingTiers ? (
                  <LoadingContainer><Spinner size="20px" /></LoadingContainer>
                ) : (
                  <TierGrid>
                    {platformTiers.filter(t => t.active).sort((a, b) => a.order - b.order).map(tier => {
                      const selected = tenant.tier === tier.tierId;
                      const price = tier.prices?.BRL?.monthly;
                      const lim = tier.limits || {};
                      return (
                        <TierCard key={tier.tierId} selected={selected} onClick={() => setTenant({ ...tenant, tier: tier.tierId })}>
                          <TierCardCheck selected={selected}>{selected && <Check size={12} />}</TierCardCheck>
                          <TierCardName>{tier.name}</TierCardName>
                          <TierCardPrice>
                            {price ? formatPrice(price, 'BRL') : t('onRequest')}
                            {price && <span>{t('perMonth')}</span>}
                          </TierCardPrice>
                          <TierCardLimits>
                            <li>{lim.conversations === -1 ? '∞' : lim.conversations} {t('conversationsPerMonth')}</li>
                            <li>{lim.agents === -1 ? '∞' : lim.agents} {lim.agents === 1 ? t('agentSingular') : t('agentPlural')}</li>
                            <li>{lim.channels === -1 ? '∞' : lim.channels} {lim.channels === 1 ? t('channelSingular') : t('channelPlural')}</li>
                            <li>{lim.members === -1 ? '∞' : lim.members} {lim.members === 1 ? t('memberSingular') : t('memberPlural')}</li>
                          </TierCardLimits>
                          <TierFeatures>
                            {tier.features?.knowledgeBase && <TierFeature>{t('featureKnowledgeBase')}</TierFeature>}
                            {tier.features?.scheduledTasks && <TierFeature>{t('featureScheduledTasks')}</TierFeature>}
                            {tier.features?.vision && <TierFeature>{t('featureVision')}</TierFeature>}
                            {tier.features?.audio && <TierFeature>{t('featureAudio')}</TierFeature>}
                            {!tier.features?.knowledgeBase && <TierFeatureOff>{t('featureKnowledgeBaseOff')}</TierFeatureOff>}
                          </TierFeatures>
                        </TierCard>
                      );
                    })}
                  </TierGrid>
                )}
                {/* Toggle cobrança anual — mesmo padrão do RegisterPage */}
                {(() => {
                  const selectedTierObj = platformTiers.find(t => t.tierId === tenant.tier);
                  const hasAnnual = !!(selectedTierObj?.prices?.[getCountryCurrency(tenant.country)]?.annual);
                  return (
                    <AnnualToggleRow style={{ opacity: hasAnnual ? 1 : 0.4, pointerEvents: hasAnnual ? 'auto' : 'none' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: hasAnnual ? 'pointer' : 'not-allowed', fontSize: theme.typography.sm }}>
                        <input
                          type="checkbox"
                          checked={billingAnnual}
                          disabled={!hasAnnual}
                          onChange={e => hasAnnual && setBillingAnnual(e.target.checked)}
                        />
                        <span>
                          {t('annualBilling', { defaultValue: 'Cobrança anual' })}{' '}
                          <strong style={{ color: theme.colors.success }}>{t('annualDiscount', { defaultValue: '2 meses grátis' })}</strong>
                        </span>
                        {!hasAnnual && tenant.tier && (
                          <span style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted }}>
                            {t('annualNotAvailable', { defaultValue: 'Não disponível neste plano' })}
                          </span>
                        )}
                      </label>
                    </AnnualToggleRow>
                  );
                })()}

                {/* Retenção de logs de conversa — apenas sallus_admin (Requirement 8) */}
                {isSallusAdmin && (
                  <FormGroup style={{ marginTop: theme.spacing[5], maxWidth: 320 }}>
                    <LabelWithHelp>
                      <Label style={{ margin: 0 }}>{t('logRetention', { defaultValue: 'Retenção de logs (dias)' })}</Label>
                      <Popover content={t('tooltips.logRetention', { defaultValue: 'Por quantos dias as transcrições de conversa ficam disponíveis para auditoria no histórico. Entre 1 e 60 dias.' })}>
                        <HelpCircle size={13} color={theme.colors.textMuted} />
                      </Popover>
                    </LabelWithHelp>
                    <Input
                      type="number"
                      min={1}
                      max={60}
                      value={tenant.logRetentionDays}
                      onChange={e => setTenant({ ...tenant, logRetentionDays: e.target.value })}
                    />
                    <Helper style={{ marginTop: theme.spacing[1] }}>
                      {t('logRetentionHint', { defaultValue: 'Entre 1 e 60 dias. Padrão: 1.' })}
                    </Helper>
                  </FormGroup>
                )}
              </Section>
            )}


            {step === 3 && (
              <Section>
                <SectionTitle>{t('step4Title')}</SectionTitle>
                <FormGroup><Label>{t('adminFullName')}</Label><Input value={tenant.adminName} onChange={e => setTenant({ ...tenant, adminName: e.target.value })} placeholder={t('adminFullNamePlaceholder')} /></FormGroup>
                <FormGroup style={{ marginTop: theme.spacing[4] }}><Label>{t('email')}</Label><Input type="email" value={tenant.adminEmail} onChange={e => setTenant({ ...tenant, adminEmail: e.target.value })} placeholder={t('adminEmailPlaceholder')} /></FormGroup>
                <FormGroup style={{ marginTop: theme.spacing[4] }}>
                  <LabelWithHelp>
                    <Label style={{ margin: 0 }}>{editMode ? t('editPassword') : t('newPassword')}</Label>
                    <Popover content={t('tooltips.adminPassword')}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
                  </LabelWithHelp>
                  <Input type="password" value={tenant.adminPassword} onChange={e => setTenant({ ...tenant, adminPassword: e.target.value })} placeholder={t('passwordPlaceholder')} />
                  <Helper style={{ marginTop: theme.spacing[1] }}>{editMode ? t('passwordHintEdit') : t('passwordHintNew')}</Helper>
                </FormGroup>
                <FormGroup style={{ marginTop: theme.spacing[4] }}>
                  <LabelWithHelp>
                    <Label style={{ margin: 0 }}>{t('billingEmail')}</Label>
                    <Popover content={t('tooltips.billingEmail', { defaultValue: t('billingEmailTooltip') })}><HelpCircle size={13} color={theme.colors.textMuted} /></Popover>
                  </LabelWithHelp>
                  <Input type="email" value={tenant.billingEmail} onChange={e => setTenant({ ...tenant, billingEmail: e.target.value })} placeholder={tenant.adminEmail || t('billingEmailPlaceholder')} />
                </FormGroup>
              </Section>
            )}

            {step === 4 && (
              <Section>
                <SectionTitle>{t('step5Title')}</SectionTitle>
                <ReviewBlock>
                  <ReviewTitle>{t('reviewClient')}</ReviewTitle>
                  <ReviewItem><strong>{t('reviewName')}</strong> {tenant.name}</ReviewItem>
                  <ReviewItem><strong>{t('reviewEmail')}</strong> {tenant.email}</ReviewItem>
                  {tenant.phone && <ReviewItem><strong>{t('reviewPhone')}</strong> {tenant.phone}</ReviewItem>}
                  {tenant.address?.city && <ReviewItem><strong>{t('reviewCity')}</strong> {tenant.address.city}{tenant.address.state ? `, ${tenant.address.state}` : ''}</ReviewItem>}
                  {tenant.document && <ReviewItem><strong>{t('reviewDocument')}</strong> {tenant.document}</ReviewItem>}
                  <ReviewItem><strong>{t('reviewCountry')}</strong> {t(`countries.${tenant.country}`, { defaultValue: tenant.country })}</ReviewItem>
                  <ReviewItem><strong>{t('reviewPersonType')}</strong> {tenant.personType === 'company' ? t('personTypeCompany') : t('personTypeIndividual')}</ReviewItem>
                </ReviewBlock>
                <ReviewBlock>
                  <ReviewTitle>{t('reviewTier')}</ReviewTitle>
                  {(() => {
                    const tier = platformTiers.find(t => t.tierId === tenant.tier);
                    const price = tier?.prices?.BRL?.monthly;
                    return (
                      <>
                        <ReviewItem><strong>{tier?.name}</strong> — {price ? formatPrice(price, 'BRL') + t('perMonth') : t('onRequest')}</ReviewItem>
                        <ReviewItem style={{ color: theme.colors.textMuted }}>
                          {tier?.limits?.conversations === -1 ? '∞' : tier?.limits?.conversations} {t('conversationsPerMonth')} &middot;&nbsp;
                          {tier?.limits?.agents === -1 ? '∞' : tier?.limits?.agents} {t('agentPlural')} &middot;&nbsp;
                          {tier?.limits?.channels === -1 ? '∞' : tier?.limits?.channels} {t('channelPlural')}
                        </ReviewItem>
                        {billingAnnual && (
                          <ReviewItem style={{ color: theme.colors.success }}>
                            ✓ {t('annualBilling', { defaultValue: 'Cobrança anual' })} — {t('annualDiscount', { defaultValue: '2 meses grátis' })}
                          </ReviewItem>
                        )}
                      </>
                    );
                  })()}
                </ReviewBlock>
                <ReviewBlock>
                  <ReviewTitle>{t('reviewAdmin')}</ReviewTitle>
                  <ReviewItem><strong>{t('reviewName')}</strong> {tenant.adminName}</ReviewItem>
                  <ReviewItem><strong>{t('reviewEmail')}</strong> {tenant.adminEmail}</ReviewItem>
                  {tenant.billingEmail && tenant.billingEmail !== tenant.adminEmail && (
                    <ReviewItem><strong>{t('billingEmail')}</strong> {tenant.billingEmail}</ReviewItem>
                  )}
                </ReviewBlock>
                <TermsNote>
                  {t('termsAdminNote', { defaultValue: 'Ao criar este cliente, você confirma que os Termos de Uso foram aceitos em nome do cliente (v1.1).' })}
                </TermsNote>
              </Section>
            )}
          </StepContent>

          <WizardActions>
            {step > 1 && <Button variant="secondary" onClick={() => setStep(step - 1)}>{t('back')}</Button>}
            <Button variant="ghost" onClick={() => navigate('/sallus/dashboard')}>{t('cancel', { ns: 'common' })}</Button>
            <div style={{ flex: 1 }} />
            {step < 4
              ? <Button variant="primary" onClick={handleNext}>{t('next')}</Button>
              : <Button variant="success" onClick={handleSubmit}>{editMode ? t('saveChanges') : t('createClient')}</Button>
            }
          </WizardActions>
        </WizardCard>
      </PageContainer>
    </>
  );
};

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const AnnualToggleRow = styled.div`margin-top: ${theme.spacing[4]}; padding: ${theme.spacing[3]}; background: ${theme.colors.gray50}; border-radius: ${theme.radii.md}; border: 1px solid ${theme.colors.border};`;
const TermsNote = styled.div`margin-top: ${theme.spacing[3]}; padding: ${theme.spacing[3]}; background: ${theme.colors.warningLight}; border: 1px solid ${theme.colors.warningBorder}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.xs}; color: #92400e;`;
const LabelWithHelp = styled.div`display: flex; align-items: center; gap: ${theme.spacing[1]};`;
const WizardCard = styled(Card)`max-width: 860px; margin: 0 auto;`;
const StepsRow = styled.div`display: flex; align-items: center; margin-bottom: ${theme.spacing[10]};`;
const StepItem = styled.div`display: flex; flex-direction: column; align-items: center; gap: ${theme.spacing[2]}; opacity: ${p => p.active ? 1 : 0.4};`;
const StepCircle = styled.div`width: 36px; height: 36px; border-radius: ${theme.radii.full}; background: ${p => p.done ? theme.colors.success : p.active ? theme.colors.primary : theme.colors.gray200}; color: white; display: flex; align-items: center; justify-content: center; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};`;
const StepLabel = styled.div`font-size: ${theme.typography.xs}; color: ${p => p.active ? theme.colors.textPrimary : theme.colors.textMuted}; font-weight: ${theme.typography.medium}; white-space: nowrap;`;
const StepLine = styled.div`flex: 1; height: 2px; background: ${p => p.done ? theme.colors.success : theme.colors.border}; margin: 0 ${theme.spacing[2]}; margin-bottom: ${theme.spacing[5]};`;
const StepContent = styled.div`min-height: 380px;`;
const Section = styled.div``;
const SectionTitle = styled.h2`font-size: ${theme.typography.xl}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[5]} 0;`;
const FormGrid = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: ${theme.spacing[4]};`;
const Helper = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted};`;
const HelperErr = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.danger}; margin-top: ${theme.spacing[1]};`;
const TierGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: ${theme.spacing[3]};`;
const TierCard = styled.div`border: 2px solid ${p => p.selected ? theme.colors.primary : theme.colors.border}; border-radius: ${theme.radii.xl}; padding: ${theme.spacing[4]}; background: ${p => p.selected ? theme.colors.primaryLight : theme.colors.white}; cursor: pointer; position: relative; transition: ${theme.transitions.fast}; &:hover { border-color: ${theme.colors.primary}; transform: translateY(-2px); }`;
const TierCardCheck = styled.div`position: absolute; top: ${theme.spacing[3]}; right: ${theme.spacing[3]}; width: 20px; height: 20px; border-radius: ${theme.radii.full}; border: 2px solid ${p => p.selected ? theme.colors.primary : theme.colors.border}; background: ${p => p.selected ? theme.colors.primary : 'white'}; color: white; display: flex; align-items: center; justify-content: center;`;
const TierCardName = styled.div`font-size: ${theme.typography.base}; font-weight: ${theme.typography.bold}; color: ${theme.colors.textPrimary}; margin-bottom: ${theme.spacing[1]};`;
const TierCardPrice = styled.div`font-size: ${theme.typography.xl}; font-weight: ${theme.typography.bold}; color: ${theme.colors.primary}; margin-bottom: ${theme.spacing[3]}; span { font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; font-weight: 400; }`;
const TierCardLimits = styled.ul`list-style: none; padding: 0; margin: 0 0 ${theme.spacing[3]} 0; display: flex; flex-direction: column; gap: 4px; font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};`;
const TierFeatures = styled.div`display: flex; flex-direction: column; gap: 2px; padding-top: ${theme.spacing[2]}; border-top: 1px solid ${theme.colors.border};`;
const TierFeature = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.success};`;
const TierFeatureOff = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const ReviewBlock = styled.div`margin-bottom: ${theme.spacing[4]}; padding: ${theme.spacing[4]}; background: ${theme.colors.gray50}; border-radius: ${theme.radii.lg};`;
const ReviewTitle = styled.h3`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[3]} 0;`;
const ReviewItem = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; margin-bottom: ${theme.spacing[1]};`;
const WizardActions = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]}; margin-top: ${theme.spacing[8]}; padding-top: ${theme.spacing[5]}; border-top: 1px solid ${theme.colors.border};`;

export default AddTenant;
